import React from 'react';
import PropTypes from 'prop-types';

import {Box, Image, Stack, Typography} from 'views/components';
import {ExpandMore, PriorityFlagBlue, PriorityFlagGrey, PriorityFlagOrange, PriorityFlagRed} from 'views/icons';
import {translate} from 'views/module';

import {CircularProgress, Menu, MenuItem} from '@mui/material';

const PriorityDropdown = (props) => {
  const {
    label,
    loading = false,
    selectedPriority,
    setSelectedPriority,
  } = props;

  const priorityIcons = {
    PRIORITY_1: PriorityFlagRed,
    PRIORITY_2: PriorityFlagOrange,
    PRIORITY_3: PriorityFlagBlue,
    PRIORITY_4: PriorityFlagGrey,
  };
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const priorities = React.useMemo(() => Object.entries(translate('career.Priority') || {}), []);
  const selectedPriorityKey = selectedPriority || 'NONE';
  const selectedPriorityTitle = translate(`career.Priority.${selectedPriorityKey}.Title`);

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleToggle = (event) => {
    const nextAnchorEl = event.currentTarget.closest('[data-dropdown-anchor="true"]');

    if (isMenuOpen) {
      closeMenu();
      return;
    }

    setAnchorEl(nextAnchorEl);
  };

  const handleOptionSelect = (priorityKey) => {
    closeMenu();

    if (priorityKey === selectedPriorityKey) {
      return;
    }

    setSelectedPriority(priorityKey === 'NONE' ? null : priorityKey);
  };

  return (
    <>
      <Stack
        data-dropdown-anchor='true'
        sx={{
          gap: '6px',
          minWidth: 0,
          alignItems: 'flex-start',
        }}
      >
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '11px',
            lineHeight: '14px',
            color: '#A8A49E',
          }}
        >
          {label}
        </Typography>
        <Stack direction='row' sx={{gap: '4px', alignItems: 'center', minWidth: 0}}>
          <Box
            component='button'
            onClick={handleToggle}
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '12px',
                lineHeight: '16px',
                color: '#000000',
                wordBreak: 'break-word',
              }}
            >
              {selectedPriorityTitle}
            </Typography>
          </Box>
          <Box
            component='button'
            onClick={handleToggle}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          >
            <ExpandMore
              sx={{
                color: '#A8A49E',
                fontSize: '16px',
                transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
        </Stack>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        MenuListProps={{disablePadding: true}}
        onClose={closeMenu}
        open={isMenuOpen}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              border: '1px solid #D9D8D5',
              boxShadow: '0px 4px 12.1px rgba(0, 0, 0, 0.05)',
              borderRadius: '4px',
            },
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Stack
          sx={{
            boxSizing: 'border-box',
            width: '128px',
            p: '14px 25px 25px 14px',
            gap: '10px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '11px',
              lineHeight: '14px',
              color: '#A8A49E',
              textTransform: 'uppercase',
            }}
          >
            {label}
          </Typography>
          {
            loading &&
            <Stack direction='row' sx={{alignItems: 'center', gap: '8px'}}>
              <CircularProgress size={16} sx={{color: '#A8A49E'}} />
            </Stack>
          }
          {
            !loading &&
            <Stack sx={{gap: '12px'}}>
              {
                priorities.map(([priorityKey, priorityValue]) => {
                  const isSelected = selectedPriorityKey === priorityKey;
                  const priorityIcon = priorityIcons[priorityKey];

                  return (
                    <MenuItem
                      key={priorityKey}
                      onClick={() => handleOptionSelect(priorityKey)}
                      selected={isSelected}
                      sx={{
                        'm': 0,
                        'px': '6px',
                        'py': '2px',
                        'minHeight': '22px',
                        'borderRadius': '4px',
                        'backgroundColor': 'transparent',
                        '&:hover': {
                          'backgroundColor': '#F7F4EF',
                        },
                        '&.Mui-selected': {
                          'backgroundColor': '#F7F4EF',
                        },
                        '&.Mui-selected:hover': {
                          'backgroundColor': '#EEE8DF',
                        },
                      }}
                    >
                      <Stack
                        direction='row'
                        alignItems='center'
                        sx={{width: '100%', gap: '8px'}}
                      >
                        <Typography
                          sx={{
                            fontWeight: isSelected ? 500 : 400,
                            fontSize: '14px',
                            lineHeight: '18px',
                            color: isSelected ? '#DA7955' : '#000000',
                          }}
                        >
                          {priorityValue.Title}
                        </Typography>
                        {
                          priorityIcon &&
                          <Image
                            alt={priorityValue.Title}
                            src={priorityIcon}
                            sx={{
                              width: '14px',
                              height: '14px',
                              flexShrink: 0,
                            }}
                          />
                        }
                      </Stack>
                    </MenuItem>
                  );
                })
              }
            </Stack>
          }
        </Stack>
      </Menu>
    </>
  );
};

PriorityDropdown.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  loading: PropTypes.bool,
  selectedPriority: PropTypes.string,
  setSelectedPriority: PropTypes.func.isRequired,
};

export default PriorityDropdown;
